import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SimpleBlogForm from '../components/SimpleBlogForm';
import { blogService } from '../services/blogService';
import { CreateBlogDto, UpdateBlogDto } from '../types/blog';

const CreateBlog: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialData, setInitialData] = useState<Partial<CreateBlogDto> | undefined>();
  const [loadingBlog, setLoadingBlog] = useState(!!id);

  const isEdit = !!id;

  useEffect(() => {
    const fetchBlog = async () => {
      if (!id) return;

      try {
        setLoadingBlog(true);
        const blog = await blogService.getBlogById(id);
        setInitialData({
          title: blog.title,
          slug: blog.slug,
          excerpt: blog.excerpt,
          featuredImage: blog.featuredImage,
          contentBlocks: blog.contentBlocks,
          status: blog.status,
          priority: blog.priority,
          tags: blog.tags,
          metaTitle: blog.metaTitle,
          metaDescription: blog.metaDescription,
          allowComments: blog.allowComments,
        });
      } catch (err) {
        setError('Failed to load blog for editing');
        console.error('Error fetching blog:', err);
      } finally {
        setLoadingBlog(false);
      }
    };

    fetchBlog();
  }, [id]);

  const handleSubmit = async (data: CreateBlogDto | UpdateBlogDto) => {
    try {
      setIsLoading(true);
      setError(null);

      if (isEdit && id) {
        await blogService.updateBlog(id, data as UpdateBlogDto);
      } else {
        await blogService.createBlog(data as CreateBlogDto);
      }

      navigate('/blogs');
    } catch (err) {
      setError(`Failed to ${isEdit ? 'update' : 'create'} blog. Please try again.`);
      console.error(`Error ${isEdit ? 'updating' : 'creating'} blog:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingBlog) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Create New Blog Post</h1>
            <p className="mt-2 text-sm text-gray-600">
              Create engaging content with text, images, and videos.
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded max-w-4xl mx-auto">
              {error}
            </div>
          )}

          <SimpleBlogForm
            initialData={initialData}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            isEdit={isEdit}
          />
        </div>
      </div>
    </Layout>
  );
};

export default CreateBlog; 